/**
 * urn.ts — one identity spine for the whole pipeline.
 *
 * Every entity, every projected instruction, and every authorization scope is
 * addressed by the same URN, so a runtime trace can always be walked back to the
 * evidence that justified it:
 *
 *   urn:egae:asset:0f3a:version:2
 *        │    │     │           │
 *        ├─ namespace
 *        ├─────── entity kind (the model slug)
 *        ├───────────── instance id
 *        └───────────────────── sub-part path
 */
export const NAMESPACE = "egae" as const;

export type Urn = `urn:${typeof NAMESPACE}:${string}`;

const URN_PATTERN = /^urn:[a-z0-9-]+:[a-z0-9:_-]+$/i;

export function isUrn(value: unknown): value is Urn {
  return typeof value === "string" && URN_PATTERN.test(value);
}

/** Build a URN from path segments under the egae namespace. */
export function urn(...segments: (string | number)[]): Urn {
  return `urn:${NAMESPACE}:${segments.map(String).join(":")}`;
}

export interface ParsedUrn {
  readonly namespace: string;
  readonly segments: readonly string[];
  readonly full: Urn;
}

export function parseUrn(value: Urn): ParsedUrn {
  const [, namespace, ...rest] = value.split(":");
  return { namespace: namespace ?? NAMESPACE, segments: rest, full: value };
}

/** The entity kind a URN addresses (first segment after the namespace). */
export function kindOf(value: Urn): string | undefined {
  return parseUrn(value).segments[0];
}

/** Project to an MCP / authorization scope — read/write/execute on the addressed thing. */
export function toScope(value: Urn, op: "read" | "write" | "execute"): string {
  return `${parseUrn(value).segments.join(":")}:${op}`;
}

/**
 * Mint a short, collision-resistant id for a new entity of `kind`.
 * Not cryptographic — just enough entropy to avoid clashes within a workspace.
 */
export function mintId(kind: string): Urn {
  const rand = crypto.randomUUID().slice(0, 8);
  return urn(kind, rand);
}
